import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/**
 * Central SEO block for every public route. One <Helmet> per page —
 * `react-helmet-async` handles de-duping the head tags across route changes.
 *
 * Props:
 *   title            — full <title> string (already includes brand suffix)
 *   description      — plain-text meta description (aim for 120–160 chars)
 *   canonicalPath?   — path portion of the canonical URL (default = current
 *                      pathname). Combined with SITE_BASE_URL to build the
 *                      absolute canonical.
 *   ogImage?         — absolute URL to a social-preview image.
 *                      Defaults to /brand/logo-primary.png at the current origin.
 *   ogType?          — Open Graph type (default "website")
 *   noIndex?         — emit <meta name="robots" content="noindex,nofollow">
 *   jsonLd?          — single JSON-LD object or an array. Each element is
 *                      emitted as its own <script type="application/ld+json">.
 */
export default function PageSEO({
    title,
    description,
    canonicalPath,
    ogImage,
    ogType = "website",
    noIndex = false,
    jsonLd,
}) {
    const { pathname } = useLocation();
    const base =
        (typeof process !== "undefined" && process.env.REACT_APP_SITE_BASE_URL) ||
        (typeof window !== "undefined" ? window.location.origin : "");
    const path = canonicalPath || pathname || "/";
    const canonical = `${base.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
    const image =
        ogImage ||
        (typeof window !== "undefined"
            ? `${window.location.origin}/brand/logo-primary.png?v=3`
            : "/brand/logo-primary.png?v=3");

    const scripts = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

    return (
        <Helmet prioritizeSeoTags>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />
            {noIndex && <meta name="robots" content="noindex,nofollow" />}

            {/* Open Graph */}
            <meta property="og:type" content={ogType} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={image} />
            <meta property="og:site_name" content="EB Construction" />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {scripts.map((obj, i) => (
                <script
                    key={i}
                    type="application/ld+json"
                    // JSON-LD must survive react-helmet-async's escaping; stringify here.
                >{JSON.stringify(obj)}</script>
            ))}
        </Helmet>
    );
}

/**
 * Strip markdown syntax → plain text and truncate to `limit` chars.
 * Used for meta descriptions derived from job/project description_md.
 */
export function stripMd(md, limit = 155) {
    if (!md) return "";
    const plain = String(md)
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/!\[[^\]]*\]\([^)]+\)/g, " ")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/^#+\s+/gm, "")
        .replace(/[*_~>]+/g, "")
        .replace(/\s+/g, " ")
        .trim();
    if (plain.length <= limit) return plain;
    return plain.slice(0, limit - 1).trimEnd() + "…";
}

/**
 * Convert markdown to a very small HTML representation for JSON-LD JobPosting
 * description (Google recommends HTML). Not a full parser — enough for
 * paragraphs, bold, and lists as we author them in the seed.
 */
export function mdToSimpleHtml(md) {
    if (!md) return "";
    const paras = String(md).split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
    return paras
        .map((p) => {
            let html = p
                .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
                .replace(/\*([^*]+)\*/g, "<em>$1</em>");
            if (/^#+\s+/.test(html)) {
                html = html.replace(/^#+\s+/, "");
                return `<h3>${html}</h3>`;
            }
            return `<p>${html}</p>`;
        })
        .join("");
}

/**
 * Extract the salary numeric from strings like "£41,000 per year".
 * Returns { value: 41000, unit: "YEAR" } or null.
 */
export function parseSalary(raw) {
    if (!raw) return null;
    const m = String(raw).match(/£\s*([\d,]+(?:\.\d+)?)/);
    if (!m) return null;
    const n = parseFloat(m[1].replace(/,/g, ""));
    if (!Number.isFinite(n) || n <= 0) return null;
    const s = String(raw).toLowerCase();
    let unit = "YEAR";
    if (/hour|hr\b/.test(s)) unit = "HOUR";
    else if (/week/.test(s)) unit = "WEEK";
    else if (/month/.test(s)) unit = "MONTH";
    else if (/day/.test(s)) unit = "DAY";
    return { value: n, unit };
}
